import { useCallback, useEffect, useRef, useState } from 'react';
import { UseOptimisticQuantityOptions, UseOptimisticQuantityResult } from './types';

const DEFAULT_DEBOUNCE_MS = 300;

// EWM-610 — owns the quantity-change UX for a single line item: optimistic local
// value, a trailing debounce that sends ONLY the final absolute quantity after the
// last click, supersede of in-flight writes (a slow older response can't clobber a
// newer value), a per-item pending flag (so only the edited row reacts — no
// site-wide freeze), `flush()` for critical boundaries (checkout / remove / unmount),
// and surfaced server errors. Generalises the duplicated localQty + useDebounce +
// confirmedQty block that lived in cart-items-table and purchase-list-item.
const useOptimisticQuantity = ({
  serverQty,
  onCommit,
  onRemove,
  removeAtOrBelow = 0,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseOptimisticQuantityOptions): UseOptimisticQuantityResult => {
  const [displayQty, setDisplayQty] = useState(serverQty);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // The last quantity we treat as authoritative (server-confirmed OR optimistically
  // sent). A `serverQty` change equal to this is our own write echoing back.
  const confirmedQty = useRef(serverQty);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Every setQty bumps the generation; a resolved/rejected commit whose generation is
  // stale is ignored, so an older in-flight response can't overwrite newer input.
  const generation = useRef(0);
  const pendingQty = useRef<number | null>(null);

  // Latest-callback refs so runCommit/flush stay referentially stable (and the
  // unmount flush uses the current closures, not a stale first-render one).
  const onCommitRef = useRef(onCommit);
  const onRemoveRef = useRef(onRemove);
  // EWM-760 — react-hooks/refs: writing ref.current during render is flagged.
  // Both refs are only ever read inside `runCommit`, which fires later from a
  // debounce timer or `flush()` — never synchronously within the same render
  // — so deferring the assignment to an effect (runs after every render,
  // before any timer/flush can fire) preserves the "always the latest
  // closure" behavior exactly.
  useEffect(() => {
    onCommitRef.current = onCommit;
    onRemoveRef.current = onRemove;
  });

  // Reconcile to a server-side change (stock clamp, external / cross-tab mutation),
  // but NEVER while the user has an unsettled local edit. `useCart.updateItem`
  // mutates the SWR cart even for a write the hook supersedes, so a superseded
  // earlier commit would otherwise push a stale quantity into `serverQty` and snap
  // `displayQty` back down mid-edit — the "qty goes up and down" bug. The optimistic
  // value wins until our own writes settle (`pendingQty` is cleared only on the final
  // successful commit), then we reconcile (e.g. to a stock clamp).
  useEffect(() => {
    if (pendingQty.current != null) return;
    if (serverQty === confirmedQty.current) return;
    confirmedQty.current = serverQty;
    setDisplayQty(serverQty);
  }, [serverQty]);

  const runCommit = useCallback(
    async (qty: number, gen: number) => {
      setIsPending(true);
      try {
        if (removeAtOrBelow != null && qty <= removeAtOrBelow && onRemoveRef.current) {
          await onRemoveRef.current();
        } else {
          await onCommitRef.current(qty);
        }
        if (gen !== generation.current) return; // superseded by a newer setQty
        confirmedQty.current = qty;
        pendingQty.current = null;
        setError(null);
      } catch (e) {
        if (gen !== generation.current) return; // a newer write owns the state now
        setError(e);
        // Keep displayQty optimistic so the user sees what they entered; a retry is
        // just another setQty.
      } finally {
        if (gen === generation.current) setIsPending(false);
      }
    },
    [removeAtOrBelow],
  );

  const setQty = useCallback(
    (qty: number) => {
      setDisplayQty(qty); // optimistic, synchronous
      pendingQty.current = qty;
      const gen = ++generation.current; // supersede any scheduled/in-flight write
      setIsPending(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        void runCommit(qty, gen);
      }, debounceMs);
    },
    [debounceMs, runCommit],
  );

  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pendingQty.current == null || pendingQty.current === confirmedQty.current) return;
    await runCommit(pendingQty.current, generation.current);
  }, [runCommit]);

  // Flush a pending write on unmount so a last-second edit (e.g. navigating away
  // mid-debounce) isn't lost. Use a ref so the cleanup always calls the latest flush.
  const flushRef = useRef(flush);
  // EWM-760 — react-hooks/refs: same latest-ref-in-effect move as above;
  // `flushRef` is only read from the unmount cleanup below, never during
  // render, so this is a no-op timing change.
  useEffect(() => {
    flushRef.current = flush;
  });
  useEffect(
    () => () => {
      void flushRef.current();
    },
    [],
  );

  return { displayQty, isPending, error, setQty, flush };
};

export default useOptimisticQuantity;
